import { type FunctionComponent, useEffect, useState } from 'react'
import type L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import { matchPath, useLocation } from 'react-router-dom'
import LeafletMap from '../LeafletMap/LeafletMap'
import SidebarMapControls from './SidebarMapControls'
import LeafletStyles from './LeafletStyles'
import useLayers from '../../../pages/MapPage/utils/useLayers'
import SidebarMapLegend from './SidebarMapLegend'
import useMapClickHandler from '../../../pages/MapPage/utils/useMapClickHandler'
import useMapPosition from '../../../pages/MapPage/utils/useMapPosition'
import useMarkers from '../../../pages/MapPage/utils/useMarkers'
import { useMapContext } from '../../contexts/map/MapContext'
import PanoramaViewer from '../../../pages/MapPage/components/PanoramaViewer/PanoramaViewer'
import { routing } from '../../../routes'
import useCenterOnFeature from '../../../pages/MapPage/utils/useCenterOnFeature'
import { useIsEmbedded } from '../../contexts/ui'

interface SidebarMapProps {
  isMobile: boolean
  onCloseMobileMap: () => void
}

const SidebarMap: FunctionComponent<SidebarMapProps> = ({ isMobile, onCloseMobileMap }) => {
  const location = useLocation()
  const isEmbedded = useIsEmbedded()
  const { showLargeMap, featureLayers, legendActive, panoActive, panoFullScreen, setLegendActive } =
    useMapContext()
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null)
  const mapLayers = useLayers()
  const handleClick = useMapClickHandler(!isMobile || panoActive)
  const [center, handleMove, zoom, handleZoom] = useMapPosition()
  const showSearchMarker =
    featureLayers.length === 0 &&
    !matchPath({ path: routing.dataDetail.path, end: true }, location.pathname)
  const markers = useMarkers(mapInstance, showSearchMarker)
  useCenterOnFeature(mapInstance)

  useEffect(() => {
    if (mapInstance) {
      // This is necessary to call, because we resize the map dynamically
      // https://leafletjs.com/reference-1.7.1.html#map-invalidatesize
      mapInstance.invalidateSize({ debounceMoveend: true })
    }
  }, [mapInstance, showLargeMap, panoActive, panoFullScreen])

  return (
    <>
      <LeafletStyles $panoActive={panoActive} $smallMap={panoFullScreen} />
      {panoActive && <PanoramaViewer isFullScreen={showLargeMap} />}
      <LeafletMap
        scale
        layers={mapLayers}
        markers={markers}
        options={{ center, minZoom: 7, scrollWheelZoom: !isEmbedded, zoom }}
        setInstance={setMapInstance}
        onClick={handleClick}
        onMove={handleMove}
        onZoom={handleZoom}
      />
      <SidebarMapControls
        isMobile={isMobile}
        map={mapInstance}
        onCloseMobileMap={onCloseMobileMap}
      />
      {legendActive && <SidebarMapLegend onClose={() => setLegendActive(false)} />}
    </>
  )
}

export default SidebarMap
